import { measure, QUALITY, thumbnail, toGray, type FrameMetrics, type GrayFrame } from "./frame-metrics"

// Reads small copies of what's ON SCREEN (the object-fit: cover crop of the
// video) a few times a second, outside React. Everything downstream — light,
// focus, motion, the car detector — works in screen space, so a box the
// detector finds lines up with the brackets drawn over the video.

export interface Crop {
  sx: number
  sy: number
  sw: number
  sh: number
}

// The part of a vw×vh video that an object-fit: cover element of ew×eh shows.
export function visibleCrop(vw: number, vh: number, ew: number, eh: number): Crop {
  if (!vw || !vh || !ew || !eh) return { sx: 0, sy: 0, sw: vw, sh: vh }
  const scale = Math.max(ew / vw, eh / vh)
  const sw = ew / scale
  const sh = eh / scale
  return { sx: (vw - sw) / 2, sy: (vh - sh) / 2, sw, sh }
}

// The detector's model input is square: feeding it the tall phone frame
// squashes the car ~2× and roughly halves its confidence (measured: 0.4 →
// 0.66–0.88, and small far-away cars only show up at all in the square). So
// it gets the CENTRAL SQUARE of the visible frame — where a framed car sits —
// and its boxes are mapped back to the full frame with `detectRegion`.
export interface Region {
  x: number
  y: number
  w: number
  h: number
}

// In landscape the central square is narrower than a whole car framed to
// fill the target, so there the model gets the WHOLE visible frame,
// letterboxed into its square (padded top and bottom, never squashed).
export interface DetectArea {
  // What part of the video goes into the model's square…
  src: Crop
  // …where in that square it's drawn (0–1)…
  dest: Region
  // …and where the square sits in the visible frame (0–1), for mapping boxes back.
  region: Region
}

export function detectArea(crop: Crop): DetectArea {
  if (crop.sh >= crop.sw) {
    const { square, region } = centralSquare(crop)
    return { src: square, dest: { x: 0, y: 0, w: 1, h: 1 }, region }
  }
  const a = crop.sw / crop.sh
  return {
    src: crop,
    dest: { x: 0, y: (1 - 1 / a) / 2, w: 1, h: 1 / a },
    region: { x: 0, y: -(a - 1) / 2, w: 1, h: a },
  }
}

export function centralSquare(crop: Crop): { square: Crop; region: Region } {
  const side = Math.min(crop.sw, crop.sh)
  const qx = crop.sx + (crop.sw - side) / 2
  const qy = crop.sy + (crop.sh - side) / 2
  return {
    square: { sx: qx, sy: qy, sw: side, sh: side },
    region: {
      x: (qx - crop.sx) / crop.sw,
      y: (qy - crop.sy) / crop.sh,
      w: side / crop.sw,
      h: side / crop.sh,
    },
  }
}

export interface FrameSample {
  t: number
  metrics: FrameMetrics
  thumb: Float32Array
  // The small grayscale copy the metrics came from (glare looks at a part of it).
  gray: GrayFrame
  // 320×320 model input: the central square of the visible frame (portrait),
  // or the whole frame letterboxed (landscape). See detectArea.
  detectCanvas: HTMLCanvasElement
  // Where that square sits in the visible frame (0–1).
  detectRegion: Region
  // The visible frame, in video pixels (to cut a sharper look at the car).
  crop: Crop
}

const DETECT_SIZE = 320

export class FrameSampler {
  private running = false
  private last = 0
  private handle: number | null = null
  private detect = document.createElement("canvas")
  private small = document.createElement("canvas")
  // Minimum time between samples; the detector raises it on slow phones.
  intervalMs = 200

  constructor(
    private video: HTMLVideoElement,
    private onSample: (sample: FrameSample) => void,
  ) {}

  start() {
    if (this.running) return
    this.running = true
    this.schedule()
  }

  stop() {
    this.running = false
    if (this.handle !== null) {
      const v = this.video as HTMLVideoElement & { cancelVideoFrameCallback?: (h: number) => void }
      if (v.cancelVideoFrameCallback) v.cancelVideoFrameCallback(this.handle)
      else clearTimeout(this.handle)
      this.handle = null
    }
  }

  private schedule() {
    if (!this.running) return
    const v = this.video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number
    }
    this.handle = v.requestVideoFrameCallback
      ? v.requestVideoFrameCallback(() => this.tick())
      : (setTimeout(() => this.tick(), 66) as unknown as number)
  }

  private tick() {
    if (!this.running) return
    const now = performance.now()
    try {
      if (now - this.last >= this.intervalMs) {
        this.last = now
        const sample = this.read(now)
        if (sample) this.onSample(sample)
      }
    } catch (e) {
      // One bad frame (a model hiccup, a browser drawing quirk) must not
      // stop the coaching for good: report it and carry on with the next.
      console.error(e)
    } finally {
      this.schedule()
    }
  }

  private read(t: number): FrameSample | null {
    const video = this.video
    const ew = video.clientWidth
    const eh = video.clientHeight
    if (!video.videoWidth || !ew || !eh) return null
    const crop = visibleCrop(video.videoWidth, video.videoHeight, ew, eh)
    const { src, dest, region } = detectArea(crop)

    if (this.detect.width !== DETECT_SIZE) {
      this.detect.width = DETECT_SIZE
      this.detect.height = DETECT_SIZE
    }
    const dctx = this.detect.getContext("2d")
    if (!dctx) return null
    if (dest.h < 1) {
      dctx.fillStyle = "#808080"
      dctx.fillRect(0, 0, DETECT_SIZE, DETECT_SIZE)
    }
    dctx.drawImage(
      video,
      src.sx,
      src.sy,
      src.sw,
      src.sh,
      dest.x * DETECT_SIZE,
      dest.y * DETECT_SIZE,
      dest.w * DETECT_SIZE,
      dest.h * DETECT_SIZE,
    )

    // Light / focus / motion look at the whole visible frame.
    const mw = QUALITY.METRICS_WIDTH
    const mh = Math.round((eh / ew) * mw)
    if (this.small.width !== mw || this.small.height !== mh) {
      this.small.width = mw
      this.small.height = mh
    }
    const sctx = this.small.getContext("2d", { willReadFrequently: true })
    if (!sctx) return null
    sctx.drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, mw, mh)
    const gray = toGray(sctx.getImageData(0, 0, mw, mh).data, mw, mh)
    return {
      t,
      metrics: measure(gray),
      thumb: thumbnail(gray),
      gray,
      detectCanvas: this.detect,
      detectRegion: region,
      crop,
    }
  }
}
